/**
 * Size categories for pull request diffs based on lines changed.
 * These categories are derived from percentile analysis of PR sizes.
 */
export type DiffSizeCategory = 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | 'unknown'

/**
 * Returns a size category for a pull request diff based on the number of lines changed.
 *
 * The percentage breakdown is visualized in this Azure dashboard:
 * https://dataexplorer.azure.com/dashboards/f70b2c54-e68e-4e47-aa5e-645b026a4b2a?p-_startTime=1hours&p-_endTime=now&tile=e48b1f9c-f236-4bc9-b4db-7169efb95e4e
 *
 * Size categories are based on percentile distribution of PR sizes:
 * - xs: 0-99 lines changed (below p50)
 * - s: 100-499 lines changed (p50+)
 * - m: 500-999 lines changed (p75+)
 * - l: 1000-4999 lines changed (p85+)
 * - xl: 5000-9999 lines changed (p95+)
 * - xxl: 10000+ lines changed (p99+)
 *
 * @param linesChanged - The total number of lines changed in the diff
 * @returns The size category for the diff
 *
 * @example
 * ```ts
 * getDiffSizeCategory(50)    // 'xs'
 * getDiffSizeCategory(250)   // 's'
 * getDiffSizeCategory(750)   // 'm'
 * getDiffSizeCategory(2000)  // 'l'
 * getDiffSizeCategory(7500)  // 'xl'
 * getDiffSizeCategory(15000) // 'xxl'
 * getDiffSizeCategory(undefined) // 'unknown'
 * ```
 */
export function getDiffSizeCategory(linesChanged: number | undefined): DiffSizeCategory {
  if (linesChanged === undefined) {
    return 'unknown'
  }

  // xs: 0-99 lines changed (below p50)
  if (linesChanged < 100) {
    return 'xs'
  }

  // s: 100-499 lines changed (p50+)
  if (linesChanged < 500) {
    return 's'
  }

  // m: 500-999 lines changed (p75+)
  if (linesChanged < 1000) {
    return 'm'
  }

  // l: 1000-4999 lines changed (p85+)
  if (linesChanged < 5000) {
    return 'l'
  }

  // xl: 5000-9999 lines changed (p95+)
  if (linesChanged < 10000) {
    return 'xl'
  }

  // xxl: 10000+ lines changed (p99+)
  return 'xxl'
}
